// Dados da tela de início de Vender (caminho B aprovado em 12/09/2026):
// indicadores em pilha (em aberto, ganhos no mês, taxa de ganho), o gráfico
// de 30 dias (em aberto e ganhos acumulados, do endpoint timeline), o dia
// (tarefas, agenda) e o quadro do funil visto de cima (primeira página de
// cada etapa, do endpoint batch). Só chamadas que já existem; falha vira
// null, nunca zero.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import KanbanAPI from 'dashboard/api/kanban';
import LeadAppointmentsAPI from 'dashboard/api/leadAppointments';
import {
  endOfDay,
  startOfDay,
} from 'dashboard/routes/dashboard/kanban/agenda/agendaHelpers';
import { toIntlLocale } from 'shared/helpers/intlLocale';

const MAX_COLUMNS = 5;
const MAX_CARDS = 4;
const CHART_W = 600;
const CHART_H = 190;
const CHART_PAD = 14;
export const EMPTY = '·';

const num = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Caminho SVG de uma série (0..n) escalada na caixa do gráfico.
export const linePath = (values, max, width = CHART_W, height = CHART_H) => {
  if (!values.length) return '';
  const top = CHART_PAD;
  const bottom = height - CHART_PAD;
  const step = values.length > 1 ? width / (values.length - 1) : 0;
  const y = value =>
    max > 0 ? bottom - ((bottom - top) * value) / max : bottom;
  return values
    .map(
      (value, index) =>
        `${index ? 'L' : 'M'}${(index * step).toFixed(1)} ${y(value).toFixed(1)}`
    )
    .join(' ');
};

export const areaPath = (values, max, width = CHART_W, height = CHART_H) => {
  const line = linePath(values, max, width, height);
  if (!line) return '';
  return `${line} L${width} ${height} L0 ${height} Z`;
};

// Variação % contra a janela anterior, na mesma regra do bloco :comparison do
// backend: anterior zerado não tem porcentagem (null), os dois zerados dão 0.
export const closedDeltaPct = (current, previous) => {
  const cur = num(current);
  const prev = num(previous);
  if (!cur && !prev) return 0;
  if (!prev) return null;
  return Math.round(((cur - prev) / prev) * 1000) / 10;
};

export function useSalesHome() {
  const { t, locale } = useI18n();
  const store = useStore();

  const funnel = ref(null);
  const timeline = ref(null);
  const reports = ref(null);
  // Falha de carregamento NÃO é "sem venda": os indicadores dizem que não
  // carregou e oferecem recarregar. `reportsForbidden` fica separado porque
  // 401/403 é "não é para você ver", que já tem frase própria.
  const reportsFailed = ref(false);
  const reportsForbidden = ref(false);
  const timelineFailed = ref(false);
  const agenda = ref({ count: null, next: null });
  const board = ref(null);

  const intl = toIntlLocale(locale.value);
  const money = new Intl.NumberFormat(intl, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const compact = new Intl.NumberFormat(intl, {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  });
  const shortTime = new Intl.DateTimeFormat(intl, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const shortDay = new Intl.DateTimeFormat(intl, {
    day: '2-digit',
    month: 'short',
  });

  const tasksToday = computed(() => store.getters['tasks/getTodayCount'] ?? 0);
  const tasksOverdue = computed(
    () => store.getters['tasks/getOverdueCount'] ?? 0
  );

  const lastDay = computed(() => timeline.value?.at(-1) || null);
  const openValue = computed(() =>
    lastDay.value ? num(lastDay.value.open_value) : null
  );
  const openCount = computed(() =>
    lastDay.value ? num(lastDay.value.open_count) : null
  );
  const wonInWindow = computed(() =>
    lastDay.value ? num(lastDay.value.won_value_cumulative) : null
  );

  const deltaPct = value => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || !parsed) return '';
    const rounded = Math.round(parsed);
    return t('HUB.ATTENDANCE.DELTA_PERCENT', {
      value: rounded > 0 ? `+${rounded}` : rounded,
    });
  };

  // Ganhos: o relatório (mês) quando a pessoa pode vê-lo; senão, o acumulado
  // da janela de 30 dias da própria série.
  const wonValueLabel = revenue => {
    if (revenue !== undefined && !reportsFailed.value) {
      return money.format(num(revenue));
    }
    if (wonInWindow.value === null) return EMPTY;
    return money.format(wonInWindow.value);
  };

  // Ganhos, perdas e receita pela DATA DE FECHAMENTO (closedInPeriod), como a
  // Central V2 e o useHubWorldStats. Antes lia salesMetrics, que recorta por
  // CRIAÇÃO do cartão: a venda de hoje de um cartão antigo não entrava.
  const stats = computed(() => {
    const closed = reports.value?.closedInPeriod;
    const previousClosed = reports.value?.closedInPreviousPeriod;
    const revenue = closed?.revenue;
    const performance = closed
      ? {
          won: closed.won,
          lost: closed.lost,
          wonPercentage: closed.conversionRate,
        }
      : null;
    const comparison = previousClosed
      ? {
          revenue: {
            previous: previousClosed.revenue,
            deltaPct: closedDeltaPct(
              closed?.revenue,
              previousClosed.revenue
            ),
          },
          won: { deltaPct: closedDeltaPct(closed?.won, previousClosed.won) },
        }
      : {};
    return {
      open: {
        error: timelineFailed.value,
        value: openValue.value === null ? EMPTY : money.format(openValue.value),
        hint:
          openCount.value === null
            ? ''
            : t(
                'HUB.SALES.OPEN_HINT',
                {
                  count: openCount.value,
                  ticket:
                    openCount.value > 0
                      ? money.format(openValue.value / openCount.value)
                      : money.format(0),
                },
                openCount.value
              ),
      },
      won: {
        // Só é erro quando as DUAS fontes caíram: com a série viva o valor da
        // janela de 30 dias é dado real, não um zero inventado.
        error: timelineFailed.value && reportsFailed.value,
        value: wonValueLabel(revenue),
        delta: deltaPct(comparison.revenue?.deltaPct),
        down: num(comparison.revenue?.deltaPct) < 0,
        // Plural com campo nomeado: vue-i18n só interpola na forma
        // t(chave, nomeados, plural).
        hint: performance
          ? t(
              'HUB.SALES.WON_HINT',
              {
                count: num(performance.won),
                previous:
                  comparison.revenue?.previous === undefined
                    ? EMPTY
                    : money.format(num(comparison.revenue.previous)),
              },
              num(performance.won)
            )
          : t('HUB.SALES.WON_WINDOW_HINT'),
      },
      rate: {
        error: reportsFailed.value,
        value: performance ? `${num(performance.wonPercentage)}%` : EMPTY,
        delta: deltaPct(comparison.won?.deltaPct),
        down: num(comparison.won?.deltaPct) < 0,
        hint: performance
          ? t('HUB.SALES.RATE_HINT', num(performance.lost), {
              count: num(performance.lost),
            })
          : t('HUB.SALES.RATE_NEEDS_REPORTS'),
      },
    };
  });

  const chart = computed(() => {
    const days = timeline.value || [];
    if (!days.length) return null;
    const open = days.map(day => num(day.open_value));
    const won = days.map(day => num(day.won_value_cumulative));
    const max = Math.max(1, ...open, ...won);
    const first = days[0];
    const last = days.at(-1);
    return {
      openLine: linePath(open, max),
      openArea: areaPath(open, max),
      wonLine: linePath(won, max),
      wonArea: areaPath(won, max),
      openLabel: compact.format(open.at(-1)),
      wonLabel: compact.format(won.at(-1)),
      range: `${shortDay.format(new Date(`${first.date}T12:00:00`))} → ${shortDay.format(new Date(`${last.date}T12:00:00`))}`,
      endY: value => {
        const bottom = CHART_H - CHART_PAD;
        return bottom - ((bottom - CHART_PAD) * value) / max;
      },
      lastOpen: open.at(-1),
      lastWon: won.at(-1),
    };
  });

  const stageOrder = computed(() => {
    const stages = funnel.value?.stages || {};
    return Object.entries(stages)
      .map(([key, stage]) => ({
        key,
        name: stage?.name || key,
        position: Number(stage?.position) || 0,
      }))
      .sort((a, b) => a.position - b.position);
  });

  const columns = computed(() => {
    if (!board.value) return [];
    return stageOrder.value.slice(0, MAX_COLUMNS).map(stage => {
      const bucket = board.value[stage.key] || {};
      const items = Array.isArray(bucket.items) ? bucket.items : [];
      return {
        key: stage.key,
        name: stage.name,
        count: num(bucket.pagination?.total_count ?? items.length),
        cards: items.slice(0, MAX_CARDS).map(item => ({
          id: item.id,
          title: item.item_details?.title || t('HUB.HOME.UNTITLED'),
          value: num(item.item_details?.value)
            ? money.format(num(item.item_details.value))
            : '',
          agent: (item.assigned_agents || [])[0]?.name || '',
        })),
      };
    });
  });

  const funnelSummary = computed(() =>
    columns.value.map(column => `${column.name} ${column.count}`).join(' · ')
  );

  const loadFunnelAndBoard = async () => {
    try {
      await store.dispatch('funnel/fetch');
    } catch {
      // sem funil, a tela diz isso
    }
    const list = store.getters['funnel/getFunnels'] || [];
    funnel.value = store.getters['funnel/getSelectedFunnel'] || list[0] || null;
    const funnelId = funnel.value?.id;
    const [timelineResult, boardResult] = await Promise.allSettled([
      KanbanAPI.getTimeline({ days: 30, funnelId }),
      funnelId ? KanbanAPI.getBatchData(funnelId) : Promise.resolve(null),
    ]);
    // Série que não voltou vira erro explícito, não lista vazia: lista vazia é
    // indistinguível de "nenhuma negociação".
    timelineFailed.value = timelineResult.status === 'rejected';
    timeline.value =
      timelineResult.status === 'fulfilled' &&
      Array.isArray(timelineResult.value?.days)
        ? timelineResult.value.days
        : [];
    board.value =
      boardResult.status === 'fulfilled' ? boardResult.value?.stages || {} : {};
  };

  const loadReports = async () => {
    try {
      // Janela explícita de 30 dias, a mesma do rótulo "em 30 dias".
      const to = Math.floor(Date.now() / 1000);
      const { data } = await KanbanAPI.getReports({
        from: to - 30 * 86400,
        to,
      });
      reports.value = data || null;
      reportsFailed.value = false;
      reportsForbidden.value = false;
    } catch (error) {
      reports.value = null;
      // 401/403 não é falha de carregamento: é o relatório que aquela pessoa
      // não pode ver, e a dica já diz isso.
      const status = error?.response?.status;
      reportsForbidden.value = status === 401 || status === 403;
      reportsFailed.value = !reportsForbidden.value;
    }
  };

  const loadAgenda = async () => {
    try {
      const now = new Date();
      const { data } = await LeadAppointmentsAPI.list({
        from: startOfDay(now).toISOString(),
        to: endOfDay(now).toISOString(),
      });
      const alive = (Array.isArray(data) ? data : []).filter(
        item => item.status !== 'cancelled'
      );
      const upcoming = alive
        .map(item => new Date(item.scheduled_at))
        .filter(d => Number.isFinite(d.getTime()) && d >= now)
        .sort((a, b) => a - b);
      agenda.value = {
        count: alive.length,
        next: upcoming.length ? shortTime.format(upcoming[0]) : null,
      };
    } catch {
      agenda.value = { count: null, next: null };
    }
  };

  // "Tentar de novo" dos indicadores: refaz as duas cargas que alimentam os três
  // números (a série do Kanban e o relatório).
  const retryStats = () => {
    timelineFailed.value = false;
    reportsFailed.value = false;
    return Promise.all([loadFunnelAndBoard(), loadReports()]);
  };

  onMounted(() => {
    store.dispatch('tasks/fetchNotifications').catch(() => {});
    loadFunnelAndBoard();
    loadReports();
    loadAgenda();
  });

  return {
    retryStats,
    funnel,
    stats,
    chart,
    columns,
    funnelSummary,
    tasksToday,
    tasksOverdue,
    agenda,
    timelineLoaded: computed(() => timeline.value !== null),
  };
}
